// IA Insights chat endpoint (ADR-0050). Tenant-scoped authed POST to
// core-api; core-api assembles the org-scoped grounding facts and proxies
// to ai-svc. The dashboard never sends portfolio context — it only sends
// the question and the prior turns.

import {
  iaChatResponse,
  storeComparisonResponse,
  storeComparisonTable,
  storeComparisonSearchParams,
  type IaChatResponse,
  type IaChatTurn,
  type StoreComparisonQuery,
  type StoreComparisonResponse,
} from "@bitacorapro/api-types";
import { request, requestDownload, type BlobDownload } from "../client";

export type { IaChatTurn };

export async function postIaChat(
  body: { question: string; history: IaChatTurn[] },
  signal?: AbortSignal
): Promise<IaChatResponse> {
  // Ingress parse, mirroring the facility-dashboard endpoint: catch
  // version skew between a cached dashboard bundle and a newer core-api.
  const json = await request<unknown>("POST", "core", "/ia/chat", {
    body,
    ...(signal ? { signal } : {}),
  });
  // ADR-0167 — the table is parsed on its own: a stale tab whose bundle
  // predates a table-shape change (or a core-api deployed ahead of it)
  // loses the table and keeps the answer, never the whole turn.
  const { table, ...rest } =
    json !== null && typeof json === "object" ? (json as Record<string, unknown>) : {};
  const answer = iaChatResponse.parse(rest);
  if (table === undefined) return answer;
  const parsedTable = storeComparisonTable.safeParse(table);
  if (!parsedTable.success) {
    console.warn("ia-chat: table dropped (shape mismatch)");
    return answer;
  }
  return { ...answer, table: parsedTable.data };
}

// «Comparar tiendas» (ADR-0167). No model involved: core-api fills the
// table from the same rows the chat reads, so tapping the chip costs no
// /ia/chat rate-limit slot.
function compareSearch(query: StoreComparisonQuery): string {
  const qs = storeComparisonSearchParams(query).toString();
  return qs ? `?${qs}` : "";
}

export async function getCompareStores(
  query: StoreComparisonQuery,
  signal?: AbortSignal
): Promise<StoreComparisonResponse> {
  const json = await request<unknown>("GET", "core", `/assets/compare${compareSearch(query)}`, {
    ...(signal ? { signal } : {}),
  });
  return storeComparisonResponse.parse(json);
}

/**
 * The same query as an .xlsx built server-side (typed cells, «Notas»
 * sheet), re-read at export time; `filename` is the server's.
 */
export function getCompareStoresXlsx(query: StoreComparisonQuery): Promise<BlobDownload> {
  return requestDownload("GET", "core", `/assets/compare.xlsx${compareSearch(query)}`);
}
